'use client';
import { Link } from '@/i18n/navigation';
import Image from 'next/image';
import React, { useEffect, useState } from 'react';
import { routes } from '../../routes';
import { useThemeStore } from '../../stores/useThemeStore';
import { getCookie } from '../../utils';

interface OrthLogoProps {
  width?: number;
  height?: number;
  className?: string;
  route?: string;
  asLink?: boolean;
  /** Force the dark-theme (light-coloured) mark, e.g. on an inverted surface. */
  inverted?: boolean;
}

const OrthLogo = ({
  width = 40,
  height = 40,
  className,
  route,
  asLink = true,
  inverted = false,
  ...props
}: OrthLogoProps & React.HTMLAttributes<HTMLAnchorElement>) => {
  const { mode } = useThemeStore();
  const href = route || routes.orth;

  // The theme is client-only, so the server always renders the light mark. The
  // first client render has to match it, otherwise React keeps the server's
  // `src` for good (hydration reconciles text, not attributes) — that is the
  // invisible dark-on-dark logo. Flipping after mount is an ordinary re-render,
  // which does patch the attribute.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  // Store `mode` is `undefined` until the store rehydrates; the `theme` cookie
  // covers that window.
  const resolvedMode =
    mode ?? (getCookie('theme') as 'Light' | 'Dark' | null) ?? 'Light';

  const isDarkLogo = (mounted && resolvedMode === 'Dark') || inverted;

  const image = (
    <Image
      src={isDarkLogo ? '/Logo-dark.svg' : '/Logo-light.svg'}
      width={width}
      height={height}
      loading='eager'
      alt='Orth'
      className={className}
      style={{ objectFit: 'contain' }}
    />
  );

  if (!asLink) return image;

  return (
    <Link href={href} {...props}>
      {image}
    </Link>
  );
};

export default OrthLogo;
